import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Activity, AlertTriangle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SyncHealthPulse — small live indicator in the global header.
 * Polls /api/sync/orchestrator/health every 20s and renders:
 *   - hidden when status='healthy' (idle, no clutter)
 *   - blue spinner + "Syncing N" when status='syncing'
 *   - red triangle + "Stuck N" when status='stuck' (>10 min no heartbeat)
 * Click → /sync-jobs.
 */
export default function SyncHealthPulse() {
  const [health, setHealth] = useState(null);
  const navigate = useNavigate();
  const intervalRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchHealth = async () => {
      try {
        const r = await axios.get(`${API}/sync/orchestrator/health`);
        if (!cancelled) setHealth(r.data);
      } catch {
        // Non-fatal: just hide the indicator on errors
        if (!cancelled) setHealth(null);
      }
    };
    fetchHealth();
    intervalRef.current = setInterval(fetchHealth, 20000);
    return () => { cancelled = true; if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  if (!health || health.status === 'healthy') return null;

  const isStuck = health.status === 'stuck';
  const Icon = isStuck ? AlertTriangle : Loader2;
  const count = isStuck ? health.stuck_count : health.active_count;
  const label = isStuck ? 'Stuck' : 'Syncing';
  const colorClass = isStuck
    ? 'bg-red-50 text-red-700 border-red-300 hover:bg-red-100'
    : 'bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100';
  const iconClass = isStuck ? '' : 'animate-spin';
  const tooltip = isStuck
    ? `${health.stuck_count} sync job(s) have not heartbeat in 10+ minutes — click to inspect`
    : `${health.active_count} sync job(s) running — click to view live progress`;

  return (
    <button
      type="button"
      onClick={() => navigate('/sync-jobs')}
      title={tooltip}
      data-testid="sync-health-pulse"
      data-status={health.status}
      className={`hidden sm:inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border font-medium transition-colors ${colorClass}`}
    >
      <span className="relative flex h-2 w-2">
        <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${isStuck ? 'bg-red-500' : 'bg-blue-500'}`} />
        <span className={`relative inline-flex rounded-full h-2 w-2 ${isStuck ? 'bg-red-600' : 'bg-blue-600'}`} />
      </span>
      <Icon className={`w-3.5 h-3.5 ${iconClass}`} />
      <span>{label} {count}</span>
    </button>
  );
}
